import { fromJS, List, Map } from 'immutable';
import { combineActions, createAction, handleActions } from 'redux-actions';
import api from '../../utils/api';
import {
  INBOUND_ESTIMATE_KEY,
  INBOUND_LABEL_KEY,
} from '../../utils/inboundShipment';
import { readTotalFromHeaders } from './utils/helpers';

export const defaultInboundParams = Map({ page: 1 });

export const initialState = Map({
  isLoading: false,
  items: List(), // List([InboundShipmentDto, ...]) — list serializer rows
  total: 0, // from the `total` response header
  params: defaultInboundParams,

  detailLoadingId: null, // id of the shipment whose detail fetch is in flight
  details: Map(), // Map { shipmentId: show record (may nest estimate/label) }

  // Map { shipmentId: fresh estimate response ({ rates, valid_weight, … }) }.
  // Deliberately transient: only a fresh estimate carries rate_ids (the show
  // serializer's persisted estimate omits them), so only this map can enable
  // "Buy label". Any save/ship/cancel/delete drops the entry.
  rateOptions: Map(),

  isAddressesLoading: false,
  addressesLoaded: false, // distinguishes "never fetched" from "user has none"
  addresses: List(), // List([InboundAddressDto, ...])

  isEstimating: false,
  isShipping: false,
  isCancelingLabel: false,
});

// ── Action types ──────────────────────────────────────────────
export const GET_INBOUND_SHIPMENTS = 'inbound/GET_INBOUND_SHIPMENTS';
export const GET_INBOUND_SHIPMENT = 'inbound/GET_INBOUND_SHIPMENT';
export const CREATE_INBOUND_SHIPMENT = 'inbound/CREATE_INBOUND_SHIPMENT';
export const UPDATE_INBOUND_SHIPMENT = 'inbound/UPDATE_INBOUND_SHIPMENT';
export const DELETE_INBOUND_SHIPMENT = 'inbound/DELETE_INBOUND_SHIPMENT';
export const ESTIMATE_INBOUND_SHIPMENT = 'inbound/ESTIMATE_INBOUND_SHIPMENT';
export const CLEAR_INBOUND_RATE_OPTIONS = 'inbound/CLEAR_INBOUND_RATE_OPTIONS';
export const SHIP_INBOUND_SHIPMENT = 'inbound/SHIP_INBOUND_SHIPMENT';
export const CANCEL_INBOUND_LABEL = 'inbound/CANCEL_INBOUND_LABEL';
export const IMPORT_INBOUND_SHIPMENTS = 'inbound/IMPORT_INBOUND_SHIPMENTS';
export const ADD_INBOUND_SHIPMENT = 'inbound/ADD_INBOUND_SHIPMENT';
export const GET_INBOUND_CSV_EXAMPLE = 'inbound/GET_INBOUND_CSV_EXAMPLE';
export const GET_INBOUND_ADDRESSES = 'inbound/GET_INBOUND_ADDRESSES';
export const CREATE_INBOUND_ADDRESS = 'inbound/CREATE_INBOUND_ADDRESS';
export const UPDATE_INBOUND_ADDRESS = 'inbound/UPDATE_INBOUND_ADDRESS';

// ── Action creators ───────────────────────────────────────────
// meta carries the requested params so SUCCESS can append (page > 1) vs replace.
export const getInboundShipments = createAction(
  GET_INBOUND_SHIPMENTS,
  (params = defaultInboundParams) =>
    api.inbound.getInboundShipments({
      page: params.get('page', 1),
      filter: params.get('filter', Map()).toJS(),
    }),
  (params = defaultInboundParams) => ({ params }),
);
export const getInboundShipment = createAction(
  GET_INBOUND_SHIPMENT,
  api.inbound.getInboundShipment,
  (id) => ({ id }),
);
export const createInboundShipment = createAction(
  CREATE_INBOUND_SHIPMENT,
  api.inbound.createInboundShipment,
);
export const updateInboundShipment = createAction(
  UPDATE_INBOUND_SHIPMENT,
  (id, data) => api.inbound.updateInboundShipment({ id, ...data }),
  (id) => ({ id }),
);
export const deleteInboundShipment = createAction(
  DELETE_INBOUND_SHIPMENT,
  api.inbound.deleteInboundShipment,
  (id) => ({ id }),
);
export const estimateInboundShipment = createAction(
  ESTIMATE_INBOUND_SHIPMENT,
  api.inbound.estimateInboundShipment,
  (id) => ({ id }),
);
export const clearInboundRateOptions = createAction(
  CLEAR_INBOUND_RATE_OPTIONS,
  (id) => id,
);
export const shipInboundShipment = createAction(
  SHIP_INBOUND_SHIPMENT,
  (id, rateId) => api.inbound.shipInboundShipment({ id, rate_id: rateId }),
  (id) => ({ id }),
);
export const cancelInboundLabel = createAction(
  CANCEL_INBOUND_LABEL,
  api.inbound.cancelInboundLabel,
  (id) => ({ id }),
);
export const importInboundShipmentsCsv = createAction(
  IMPORT_INBOUND_SHIPMENTS,
  ({ file, inboundAddressId }) => {
    const data = new FormData();
    data.append('file', file);
    data.append('inbound_address_id', String(inboundAddressId));
    return api.inbound.importInboundShipmentsCsv(data);
  },
);
// Plain action for a shipment the backend created outside the create/import
// flows (inbound_shipment_parse jobs). Payload is the plain shipment JSON.
export const addInboundShipment = createAction(
  ADD_INBOUND_SHIPMENT,
  (shipment) => fromJS({ data: shipment }),
);
export const getInboundCsvExample = createAction(
  GET_INBOUND_CSV_EXAMPLE,
  api.inbound.getInboundCsvExample,
);
export const getInboundAddresses = createAction(
  GET_INBOUND_ADDRESSES,
  api.inbound.getInboundAddresses,
);
export const createInboundAddress = createAction(
  CREATE_INBOUND_ADDRESS,
  api.inbound.createInboundAddress,
);
export const updateInboundAddress = createAction(
  UPDATE_INBOUND_ADDRESS,
  (id, data) => api.inbound.updateInboundAddress({ id, ...data }),
  (id) => ({ id }),
);

// ── Reducer ───────────────────────────────────────────────────
const inboundReducer = handleActions(
  {
    [`${GET_INBOUND_SHIPMENTS}_REQUEST`]: (state) =>
      state.set('isLoading', true),
    [`${GET_INBOUND_SHIPMENTS}_SUCCESS`]: (state, { payload, meta }) => {
      const params = meta.params || defaultInboundParams;
      const isPagination = params.get('page', 1) > 1;
      const total =
        parseInt(readTotalFromHeaders(payload.get('headers')), 10) || 0;

      return state
        .update('items', (items) =>
          isPagination
            ? items.concat(payload.get('data', List()))
            : payload.get('data', List()),
        )
        .set('total', total)
        .set('params', params)
        .set('isLoading', false);
    },
    [`${GET_INBOUND_SHIPMENTS}_ERROR`]: (state) =>
      state.set('isLoading', false),

    [`${GET_INBOUND_SHIPMENT}_REQUEST`]: (state, { meta }) =>
      state.set('detailLoadingId', meta.id),
    [`${GET_INBOUND_SHIPMENT}_SUCCESS`]: (state, { payload, meta }) => {
      const shipment = payload.get('data');
      const next = state.setIn(['details', shipment.get('id')], shipment);
      // A stale response must not clear the spinner of a newer fetch.
      return next.get('detailLoadingId') === meta.id
        ? next.set('detailLoadingId', null)
        : next;
    },
    [`${GET_INBOUND_SHIPMENT}_ERROR`]: (state, { meta }) =>
      state.get('detailLoadingId') === meta.id
        ? state.set('detailLoadingId', null)
        : state,

    [combineActions(
      `${CREATE_INBOUND_SHIPMENT}_SUCCESS`,
      `${IMPORT_INBOUND_SHIPMENTS}_SUCCESS`,
      ADD_INBOUND_SHIPMENT,
    )]: (state, { payload }) => {
      const shipment = payload.get('data');
      return state
        .update('items', (items) => items.unshift(shipment))
        .update('total', (total) => total + 1)
        .setIn(['details', shipment.get('id')], shipment);
    },

    // The panel refetches the detail after an update — the show response is
    // the source of truth, so only the list row is merged here. The server
    // destroys the persisted estimate whenever packages are sent (and our
    // save path always sends them), so the stale estimate and any fresh
    // rates are dropped immediately instead of waiting for the refetch.
    [`${UPDATE_INBOUND_SHIPMENT}_SUCCESS`]: (state, { payload, meta }) =>
      state
        .update('items', (items) =>
          items.map((row) =>
            row.get('id') === meta.id ? row.merge(payload.get('data')) : row,
          ),
        )
        .deleteIn(['details', meta.id, INBOUND_ESTIMATE_KEY])
        .deleteIn(['rateOptions', meta.id]),

    [`${DELETE_INBOUND_SHIPMENT}_SUCCESS`]: (state, { meta }) =>
      state
        .update('items', (items) =>
          items.filter((row) => row.get('id') !== meta.id),
        )
        .update('total', (total) => Math.max(total - 1, 0))
        .deleteIn(['details', meta.id])
        .deleteIn(['rateOptions', meta.id]),

    [`${ESTIMATE_INBOUND_SHIPMENT}_REQUEST`]: (state) =>
      state.set('isEstimating', true),
    // The rates response is not the show record's estimate — the backend
    // persisted the cheapest rate, which the next detail refetch brings in.
    [`${ESTIMATE_INBOUND_SHIPMENT}_SUCCESS`]: (state, { payload, meta }) =>
      state
        .setIn(['rateOptions', meta.id], payload.get('data'))
        .set('isEstimating', false),
    [`${ESTIMATE_INBOUND_SHIPMENT}_ERROR`]: (state) =>
      state.set('isEstimating', false),

    [CLEAR_INBOUND_RATE_OPTIONS]: (state, { payload }) =>
      state.deleteIn(['rateOptions', payload]),

    [`${SHIP_INBOUND_SHIPMENT}_REQUEST`]: (state) =>
      state.set('isShipping', true),
    // The ship response seeds the label so the panel repaints without a draft
    // flash; the follow-up detail refetch replaces it with the show shape.
    [`${SHIP_INBOUND_SHIPMENT}_SUCCESS`]: (state, { payload, meta }) =>
      state
        .setIn(['details', meta.id, INBOUND_LABEL_KEY], payload.get('data'))
        .deleteIn(['rateOptions', meta.id])
        .set('isShipping', false),
    [`${SHIP_INBOUND_SHIPMENT}_ERROR`]: (state) =>
      state.set('isShipping', false),

    [`${CANCEL_INBOUND_LABEL}_REQUEST`]: (state) =>
      state.set('isCancelingLabel', true),
    [`${CANCEL_INBOUND_LABEL}_SUCCESS`]: (state, { meta }) =>
      state
        .deleteIn(['details', meta.id, INBOUND_LABEL_KEY])
        .deleteIn(['rateOptions', meta.id])
        .set('isCancelingLabel', false),
    [`${CANCEL_INBOUND_LABEL}_ERROR`]: (state) =>
      state.set('isCancelingLabel', false),

    [`${GET_INBOUND_ADDRESSES}_REQUEST`]: (state) =>
      state.set('isAddressesLoading', true),
    [`${GET_INBOUND_ADDRESSES}_SUCCESS`]: (state, { payload }) =>
      state
        .set('addresses', payload.get('data', List()))
        .set('addressesLoaded', true)
        .set('isAddressesLoading', false),
    [`${GET_INBOUND_ADDRESSES}_ERROR`]: (state) =>
      state.set('isAddressesLoading', false),

    [`${CREATE_INBOUND_ADDRESS}_SUCCESS`]: (state, { payload }) =>
      state.update('addresses', (addresses) =>
        addresses.push(payload.get('data')),
      ),
    [`${UPDATE_INBOUND_ADDRESS}_SUCCESS`]: (state, { payload, meta }) =>
      state.update('addresses', (addresses) =>
        addresses.map((address) =>
          address.get('id') === meta.id ? payload.get('data') : address,
        ),
      ),
  },
  initialState,
);

export default inboundReducer;
